import React from 'react';
import { Helmet } from 'react-helmet-async';
import './WhyProstay.css'
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../LanguagePrefix';



function WhyProstay() {
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();

    return (
        <div>
            <Helmet>
                <title>Why Choose Prostay| All-in-One Hotel Management Platform </title>
                <meta name="description" content="Explore why modern hotels and resorts are choosing Prostay: a fully integrated hospitality platform with PMS, POS, booking engine, messaging, accounting &amp; more." />
                <link rel="canonical" href={"https://www.prostay.com" + langPrefix + "/discover-prostay/"} />

                <link rel="alternate" href="https://www.prostay.com/en/discover-prostay/" />
                <link rel="alternate" href="https://www.prostay.com/es/discover-prostay/" />

                <meta property="og:title" content="Why Choose Prostay| All-in-One Hotel Management Platform" />
                <meta property="og:description" content="Review the terms and conditions for using the Prostay platform, including usage, limitations, and user responsibilities." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={"https://www.prostay.com" + langPrefix + "/discover-prostay/"} />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Why Choose Prostay| All-in-One Hotel Management Platform" />
                <meta name="twitter:description" content="Review the terms and conditions for using the Prostay platform, including usage, limitations, and user responsibilities." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />
            </Helmet>
            <div className='why-prostay'>
                <div className='background-image'>
                    <div className='background-image-start'>
                        <div className='mt-2 d-flex align-items-center gap-1 breadcrumbs'>
                            <a href={`${langPrefix}/`} style={{ textDecoration: 'none', display: 'flex' }}><span style={{ cursor: 'pointer' }}>{t('Home')} </span></a>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6.66797 5.33337L9.33464 8.00004L6.66797 10.6667" stroke="#606576" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span style={{ color: "#1643E0" }} >{t('Discover Prostay')} </span>
                        </div>
                        <div className='mt-2'>
                            <h1>
                                {t('Discover Prostay')}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className='border-margin'>
                    <div className='custom-container'>
                        <h4>Discover Why Prostay is the Ultimate Choice for Your Property</h4>
                        <h5>Introduction</h5>
                        <p class="p3">In an increasingly competitive hospitality landscape, your technology stack is no longer just a support tool—it's a growth engine. Today’s guests expect instant communication, seamless booking experiences, and personalized service. Your staff expects software that’s intuitive, fast, and reliable. And your management team needs tools that scale with the business, not against it.</p>
                        <p class="p3">Prostay was built with all of that in mind. More than just a Property Management System (PMS), Prostay is a fully integrated, modular hospitality platform—designed from the ground up to give hotels, resorts, and vacation rentals everything they need to succeed in the modern era.</p>
                        <p class="p3">Whether you manage a boutique hotel, a remote eco-lodge, or a large resort with multiple outlets, here's why Prostay is the ultimate choice for your property.</p>
                        <h5>1. Property Management System (PMS)</h5>
                        <p class="p3">At the heart of Prostay lies a modern, intuitive PMS that gives you full control over reservations, guest folios, housekeeping, room statuses, and reporting.</p>
                        <ul class="ul1">
                            <li class="li4">Reservation creation, editing, and calendar view</li>
                            <li class="li4">Multi-room and group bookings</li>
                            <li class="li4">Folio and billing management</li>
                            <li class="li4">Room assignment and status automation</li>
                            <li class="li4">Housekeeping dashboard with tasks and real-time updates</li>
                            <li class="li4">User permissions and audit logs</li>
                            <li class="li4">Multi-property support</li>
                        </ul>
                        <h5>2. Booking Engine</h5>
                        <p class="p3">Prostay’s direct booking engine is built to help you reduce OTA dependency and increase profit margins through direct reservations.</p>
                        <ul class="ul1">
                            <li class="li4">Mobile-first, multilingual interface</li>
                            <li class="li4">Promo code and package support</li>
                            <li class="li4">Real-time availability and pricing</li>
                            <li class="li4">Seamless integration with PMS</li>
                            <li class="li4">Secure payment gateway integration</li>
                            <li class="li4">Custom branding and styling options</li>
                            <li class="li4">Google/Facebook tracking &amp; analytics compatibility</li>
                        </ul>
                        <h5>3. Channel Manager</h5>
                        <p class="p3">Eliminate overbookings and rate mismatches with Prostay’s real-time channel manager.</p>
                        <ul class="ul1">
                            <li class="li4">Two-way integration with OTAs (e.g., Booking.com, Expedia)</li>
                            <li class="li4">Real-time syncing of availability, rates, and restrictions</li>
                            <li class="li4">Bulk rate updates across all channels</li>
                            <li class="li4">Allotment and stop-sell management</li>
                            <li class="li4">Seamless link with PMS for accurate inventory</li>
                        </ul>
                        <h5>4. Prostay Nexus – Messaging Platform</h5>
                        <p class="p3">Guests expect clear, fast communication—and Prostay Nexus delivers.</p>
                        <ul class="ul1">
                            <li class="li4">Centralize email and WhatsApp communication</li>
                            <li class="li4">Auto-link every conversation to a reservation</li>
                            <li class="li4">Use templates, automation, and smart replies</li>
                            <li class="li4">Assign messages to team members</li>
                            <li class="li4">Set up auto-responses and pre-arrival flows</li>
                            <li class="li4">Built-in guest profiles and message history</li>
                        </ul>
                        <h5>5. Tableview – Integrated Point of Sale (POS)</h5>
                        <p class="p3">Prostay’s proprietary POS system, Tableview, is made for F&amp;B outlets, retail stores, and in-room service—fully integrated with the PMS.</p>
                        <ul class="ul1">
                            <li class="li4">Process orders for restaurant, bar, spa, and more</li>
                            <li class="li4">Post charges directly to guest folios in real time</li>
                            <li class="li4">Table layouts, split bills, modifiers, and discounts</li>
                            <li class="li4">Multi-location and multi-terminal setup</li>
                            <li class="li4">Compatible with Windows, Android tablets, and iPads</li>
                            <li class="li4">Works offline and syncs when reconnected</li>
                            <li class="li4">Connect to printers, cash drawers, and barcode scanners</li>
                        </ul>
                        <h5>6. Procurement Module</h5>
                        <p class="p3">Keep control of your purchasing and supplier management.</p>
                        <ul class="ul1">
                            <li class="li4">Create and track purchase orders</li>
                            <li class="li4">Manage suppliers and price lists</li>
                            <li class="li4">Monitor stock levels and delivery status</li>
                            <li class="li4">Department-specific purchasing workflows</li>
                            <li class="li4">Approval flows for budget control</li>
                            <li class="li4">Link purchasing data to POS and Accounting</li>
                        </ul>
                        <h5>7. Accounting &amp; Finance</h5>
                        <p class="p3">Prostay’s built-in financial module gives you visibility into your revenue, expenses, and guest transactions.</p>
                        <ul class="ul1">
                            <li class="li4">Real-time folio and ledger management</li>
                            <li class="li4">Income, tax, and payment tracking</li>
                            <li class="li4">Financial reports by outlet, department, or channel</li>
                            <li class="li4">Journal entries, closing tools, and reconciliation</li>
                            <li class="li4">Multi-currency and tax rule support</li>
                            <li class="li4">Integration-ready for external accounting platforms</li>
                        </ul>
                        <h5>8. Membership &amp; Loyalty Module</h5>
                        <p class="p3">Reward your most loyal guests and turn them into repeat customers.</p>
                        <ul class="ul1">
                            <li class="li4">Set up tiered membership levels</li>
                            <li class="li4">Offer custom perks (free nights, discounts, upgrades)</li>
                            <li class="li4">Track guest history and point balances</li>
                            <li class="li4">Promote exclusive offers to members</li>
                            <li class="li4">Link loyalty data to booking engine and POS</li>
                            <li class="li4">Personalize guest experiences automatically</li>
                        </ul>
                        <h5>9. Hybrid Infrastructure: Cloud, On-Premise &amp; Offline Mode</h5>
                        <p class="p3">Unlike many SaaS-only providers, Prostay supports both cloud-based and on-premise deployments. Even better? Offline mode ensures your team can keep working even during internet outages—ideal for remote resorts or areas with unreliable connectivity.</p>
                        <p class="p3">The app is native to Windows, macOS, iPadOS, and Android, so your staff can use the devices that fit your environment.</p>
                        <h5>10. Hands-On Onboarding &amp; Ongoing Support</h5>
                        <p class="p3">We don’t just hand you software—we walk with you every step of the way.</p>
                        <ul class="ul1">
                            <li class="li4">Full onboarding and system configuration</li>
                            <li class="li4">Personalized training for your team</li>
                            <li class="li4">Dedicated account management</li>
                            <li class="li4">Feature request support</li>
                            <li class="li4">Multi-language support team</li>
                        </ul>
                        <p class="p5"><br /></p>
                        <p class="p3">With Prostay, you get more than just a PMS—you get a full operating system for your hospitality business. Every module is built to integrate seamlessly, every feature is designed for efficiency, and every decision is made with hoteliers in mind.</p>
                        <p class="p3">Whether you’re upgrading from a legacy system or starting fresh, now is the time to choose a platform that grows with you.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WhyProstay;
